import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Col, Row, Container, Image, Breadcrumb, Button, Card, Modal, Form } from 'react-bootstrap';
import artTypes from '../../components/ArtTypes';
import './ArtsPage.css';
import { useAppSelector } from '../../redux/hooks';
import { selectAuthenticatedUser } from '../../redux/slices/auth.slice';
import { useGetAllArtsMutation } from '../../services/arts.service';
import { useImportImdbMovieMutation } from '../../services/import.service';
import { IArt } from '../../interfaces/arts.interface';;

const ArtsPage: React.FC = () => {
    const navigate = useNavigate();

    const artImageUrl = (url: string) => {
        if (url) {
            return url;
        }

        return '/images/movie-default.webp';
    }

    const [getAllArts, { data, error }] = useGetAllArtsMutation();
    const [importImdbMovie] = useImportImdbMovieMutation();
    const [arts, setArts] = useState<IArt[]>([]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [imdbId, setImdbId] = useState('');
    const [importing, setImporting] = useState(false);
    const { type: category } = useParams();
    const { access_token, role } = useAppSelector(selectAuthenticatedUser);
    const isAdmin = role === 'admin';

    useEffect(() => {
        getAllArts(null);
    }, [getAllArts]);

    useEffect(() => {
        if (data && !error) {
            const filteredArts = (data as any).filter((art: IArt) => art.category === category);
            setArts(filteredArts);
        } else if (error) {
            console.log(`MayArticlesPage:: Error getting articles`, error);
        }
    }, [data, error, category]);

    const chosenArtCategory = artTypes.find(artType => artType.type === category);

    return (
        <Container>
            <Row>
                <Col>
                    <Breadcrumb>
                        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                        <Breadcrumb.Item href="/database">Database</Breadcrumb.Item>
                        <Breadcrumb.Item active>{chosenArtCategory?.title}</Breadcrumb.Item>
                    </Breadcrumb>
                    <div className="art-database-heading">
                        <h2>
                            <Image src={chosenArtCategory?.image} roundedCircle />
                            {chosenArtCategory?.title} Database
                        </h2>
                    </div>
                    <hr />

                    {access_token && (
                        <div className="database-action-buttons">
                            {/* TODO: Implement Add Art functionality */}
                            {/* <Link to={`/arts/add/${category}`}>
                                <Button variant="primary" size="sm">Add a new {chosenArtCategory?.title}</Button>
                            </Link> */}
                            
                            {/* TODO: Implement My Entries functionality */}
                            {/* <Button variant="primary" size="sm">My entries</Button> */}
                            
                            {isAdmin && category === 'movie' && (
                                <Button 
                                    variant="success" 
                                    size="sm" 
                                    onClick={() => setShowImportModal(true)}
                                >
                                    Import from IMDB
                                </Button>
                            )}
                        </div>
                    )}
                    <br />
                </Col>
            </Row>
            <Row className='arts-list' md={4}>
                {arts.map((art) => (
                    <Col key={art.id}>
                        <Card className="card art-card" onClick={() => navigate(`/arts/view/${art.id}`)}>
                            <Card.Img variant="top" src={artImageUrl(art.imageUrl)} />
                            <Card.Body>
                                <Card.Title>{art.title} ({art.year})</Card.Title>
                            </Card.Body>
                            <Link to={`/arts/view/${art.id}`}>
                                <Button size='sm' variant="primary">Read More</Button>
                            </Link>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* Import Modal */}
            <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Import Movie from IMDB</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>IMDB ID</Form.Label>
                            <Form.Control 
                                type="text" 
                                placeholder="Enter IMDB ID (e.g., tt0111161)" 
                                value={imdbId}
                                onChange={(e) => setImdbId(e.target.value)}
                            />
                            <Form.Text className="text-muted">
                                Enter the IMDB ID of the movie you want to import.
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImportModal(false)}>
                        Close
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={async () => {
                            if (imdbId) {
                                // Check if movie already exists
                                const existingMovie = arts.find(art => art.imdbId === imdbId);
                                if (existingMovie) {
                                    alert(`Movie with IMDB ID ${imdbId} already exists: ${existingMovie.title}`);
                                    return;
                                }
                                
                                setImporting(true);
                                try {
                                    await importImdbMovie(imdbId);
                                    await getAllArts(null);
                                    setImdbId('');
                                    setShowImportModal(false);
                                } catch (error) {
                                    console.error('Error importing movie:', error);
                                    alert('Error importing movie. Please check the IMDB ID and try again.');
                                } finally {
                                    setImporting(false);
                                }
                            }
                        }}
                        disabled={!imdbId || importing}
                    >
                        {importing ? 'Importing...' : 'Import'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ArtsPage;
